import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useTranslation } from '../hooks/useTranslation';
import { useStatic } from '../context/StaticContext';
import { useMaintenanceMode } from '../context/MaintenanceContext';
import SuccessAlert from './alerts/SuccessAlert';
import ErrorAlert from './alerts/ErrorAlert';
import WarningAlert from './alerts/WarningAlert';
import InfoAlert from './alerts/InfoAlert';
import RulesPopup from './RulesPopup';
import { useCss } from '../context/CssContext';
import { getCategoryIcon, normalizeCat } from '../helper/categoryIcon';

const BonusConfirmPopup = ({ isOpen, onClose, bonus, onConfirm }) => {
  const { cardLayoutMode } = useCss();
  const isTextOnly = cardLayoutMode === 'text-only';
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('confirm'); // 'confirm' | 'loading' | 'result'
  const [result, setResult] = useState({ type: '', message: '' });
  const [showRedirectConfirm, setShowRedirectConfirm] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showRulesInline, setShowRulesInline] = useState(false);
  const [alertData, setAlertData] = useState({ show: false, type: null, message: '' });
  const { t } = useTranslation();
  const { selectedLanguage, isLanguage } = useStatic();
  const { isMaintenanceMode } = useMaintenanceMode();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Başka bir sitenin iframe'i içinde mi yükleniyoruz? (index.html erkenden
  // html.iframe-embedded class'ını ekliyor). Iframe içinde görünür alan parent
  // sitenin üst/alt menüleri yüzünden küçülüyor; modal'ı buna göre kompaktlaştırıp
  // butonları her zaman erişilebilir tutmamız gerekiyor.
  const [isEmbedded] = useState(() => {
    try {
      return window.self !== window.top ||
        document.documentElement.classList.contains('iframe-embedded');
    } catch (_) {
      return true;
    }
  });

  // Mobil ekran boyutu kontrolü
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dinamik çeviri fonksiyonu (MyMemory API kullanarak)
  const translateMessage = async (text, targetLang) => {
    // isLanguage yoksa veya false ise çevirme yapma
    if (!isLanguage || !targetLang || targetLang === 'tr-TR') {
      return text; // Türkçe ise veya dil desteği kapalıysa çevirme
    }

    try {
      // Dil kodunu API formatına çevir (tr-TR -> tr|en)
      const langMap = {
        'en-US': 'tr|en',
        'de-DE': 'tr|de', 
        'fr-FR': 'tr|fr',
        'es-ES': 'tr|es',
        'it-IT': 'tr|it',
        'pt-PT': 'tr|pt'
      };

      const langPair = langMap[targetLang];
      if (!langPair) return text;

      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`
      );
      
      const data = await response.json();
      
      if (data.responseStatus === 200 && data.responseData.translatedText) {
        return data.responseData.translatedText;
      }
      
      return text; // Çeviri başarısızsa orijinal metni döndür
    } catch (error) {
      console.warn('Translation failed:', error);
      return text; // Hata durumunda orijinal metni döndür
    }
  };

  // URL kontrolü için yardımcı fonksiyon
  const isValidUrl = (string) => {
    if (!string || typeof string !== 'string') return false;
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  // Popup kapandığında state'leri temizle, açıldığında redirect_link varsa yönlendirme onayını direkt aç
  useEffect(() => {
    if (!isOpen) {
      setShowRedirectConfirm(false);
      setRedirectUrl(null);
      setLoading(false);
      setImageError(false);
      setShowRulesInline(false);
      setPhase('confirm');
      setResult({ type: '', message: '' });
      setAlertData({ show: false, type: null, message: '' });
    } else if (bonus && bonus.redirect_link && isValidUrl(bonus.redirect_link)) {
      setRedirectUrl(bonus.redirect_link);
      setShowRedirectConfirm(true);
    }
  }, [isOpen, bonus]);

  // Modal açıkken arka plan (bonus listesi) scroll'unu TAMAMEN kilitle.
  // Sadece #root'u kilitlemek iframe içinde yetmiyordu: iframe-embedded modda asıl
  // scroll konteyneri body/html olduğundan, modal açıkken parmak hareketi arka
  // sayfaya (bonus listesine) sızıyordu. Body'yi pozisyonu koruyarak sabitliyoruz
  // (iOS Safari'de overflow:hidden tek başına yetmez), kapanışta scroll'u geri veriyoruz.
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Yönlendirme onayı
  const handleRedirectConfirm = () => {
    if (redirectUrl) {
      window.open(redirectUrl, '_blank');
      setShowRedirectConfirm(false);
      setRedirectUrl(null);
      onClose();
    }
  };

  const handleRedirectCancel = () => {
    setShowRedirectConfirm(false);
    setRedirectUrl(null);
  };

  const handleConfirmBonus = async () => {
    // Eğer zaten işlem yapılıyorsa, tekrar istek atma
    if (loading) {
      return;
    }

    // Eğer bonus verisinde redirect_link varsa ve geçerli bir URL ise, onay mesajı göster
    if (bonus.redirect_link && isValidUrl(bonus.redirect_link)) {
      setRedirectUrl(bonus.redirect_link);
      setShowRedirectConfirm(true);
      return;
    }
    setPhase('loading');
    try {
      const payload = { process_id: bonus.process_name, user_id: getUserIdFromUrl(), recaptcha: "test" };
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('bakim') === 't' || isMaintenanceMode) payload.dev = "10";

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      const response = await fetch(`${config.apiBaseUrl2}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', "hk": keys[0], "hh": keys[1] },
        body,
      });
      const data = await response.json();

      if (data.redirect_link && isValidUrl(data.redirect_link)) {
        setRedirectUrl(data.redirect_link);
        setPhase('confirm');
        setShowRedirectConfirm(true);
        return;
      }

      let msgRaw = '';
      let resType = '';
      const msgLower = (data.message || '').toLowerCase();
      const isQueuedMsg = msgLower.includes('sıraya alındı') || msgLower.includes('siraya alindi') || msgLower.includes('sıraya alindi') || msgLower.includes('siraya alındı');

      if (isQueuedMsg) {
        msgRaw = data.message || 'İşleminiz sıraya alındı.';
        resType = 'queued';
      } else if (data.status === true) {
        msgRaw = data.message || 'Bonus talebi başarıyla onaylandı!';
        resType = 'success';
      } else if (data.status === 3)    { msgRaw = data.message || 'Uyarı: bir durum mevcut.'; resType = 'warning'; }
      else if (data.status === 4)    { msgRaw = data.message || 'Bilgilendirme.'; resType = 'info'; }
      else                           { msgRaw = data.message || 'Talebiniz onaylanamadı.'; resType = 'error'; }

      const msg = await translateMessage(msgRaw, selectedLanguage);
      setResult({ type: resType, message: msg });
      setPhase('result');
    } catch (err) {
      console.error('Bonus onayı hatası:', err);
      const msg = await translateMessage('Bir hata oluştu. Lütfen daha sonra tekrar deneyin.', selectedLanguage);
      setResult({ type: 'error', message: msg });
      setPhase('result');
    }
  };

  // Alert tipine göre uygun bileşeni göster
  const renderAlert = () => {
    if (!alertData.show) return null;
    
    switch (alertData.type) {
      case 'success':
        return <SuccessAlert message={alertData.message} />;
      case 'warning':
        return <WarningAlert message={alertData.message} />;
      case 'info':
        return <InfoAlert message={alertData.message} />;
      case 'error':
      default:
        return <ErrorAlert message={alertData.message} />;
    }
  };

  const cs = confirmStyles;

  // Iframe modunda talep ekranı: TELEFON GÖRÜNÜR ALANINA ORTALI, SCROLL YOK,
  // tüm elementler kompakt (modal her telefon boyutunda tek ekrana sığsın).
  // Parent sitenin iframe'i alt nav'ın arkasına uzandığı için iframe ortası
  // görünür alanın ortası DEĞİL → centering'i yukarı kaydırmak için alt padding
  // dvh ile orantılı; tüm sınırlar dvh/svh ile responsive. Arka plan kilitli.
  const overlayStyle = isEmbedded
    ? { ...cs.overlay, padding: '10px 14px 22dvh' }
    : cs.overlay;

  const popupStyle = {
    ...cs.popup,
    ...(isMobile ? { maxWidth: '340px' } : {}),
    // Scroll YOK: içerik tek ekrana sığar (görsel maxHeight ile sınırlı).
    ...(isEmbedded ? { maxHeight: '94dvh', overflow: 'hidden' } : {}),
  };

  const contentStyle = isEmbedded
    ? { ...cs.content, flex: '0 0 auto', minHeight: 'auto', overflowY: 'hidden', padding: '12px 16px 4px', gap: '8px' }
    : cs.content;

  // Görsel doğal oranında, küçük max-height cap ile sınırlı → popup kompakt
  // kalır, butonlar her telefonda görünür kalır, scroll gerekmez. Cap dvh/svh
  // ile orantılı → küçük telefonlarda otomatik küçülür.
  const imgStyle = isEmbedded
    ? { ...cs.img, width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: 'min(28svh, 170px)', aspectRatio: '1 / 1', objectFit: 'cover' }
    : { ...cs.img, aspectRatio: '1 / 1' };

  const placeholderStyle = isEmbedded
    ? { ...cs.imgPlaceholder, width: '130px', height: '130px', aspectRatio: '1 / 1' }
    : { ...cs.imgPlaceholder, aspectRatio: '1 / 1' };

  // Iframe modunda tüm modal elementleri kompakt (her telefonda tek ekrana sığsın).
  const titleStyle = isEmbedded ? { ...cs.title, fontSize: '17px', lineHeight: 1.25 } : cs.title;
  const questionStyle = isEmbedded ? { ...cs.question, fontSize: '12.5px', lineHeight: 1.45 } : cs.question;
  const rulesBtnStyle = isEmbedded ? { ...cs.rulesBtn, padding: '5px 12px', fontSize: '11px' } : cs.rulesBtn;
  const actionsStyle = isEmbedded ? { ...cs.actions, padding: '8px 14px 12px', gap: '8px' } : cs.actions;
  const cancelBtnStyle = isEmbedded ? { ...cs.cancelBtn, padding: '9px 0', fontSize: '12.5px' } : cs.cancelBtn;
  const confirmBtnStyle = isEmbedded ? { ...cs.confirmBtn, padding: '9px 0', fontSize: '12.5px' } : cs.confirmBtn;

  /* ── Loading State ── */
  if (phase === 'loading') {
    return createPortal(
      <div style={overlayStyle} className="modal-overlay-anim">
        <div style={popupStyle} className="modal-popup-anim">
          <div className="modal-spinner-wrap">
            {/* Spinner */}
            <div className="modal-spinner-inner">
              <div className="modal-spinner-outer"/>
              <div className="modal-spinner-mid"/>
              <div className="modal-spinner-dot"/>
            </div>
            <div style={cs.loadingTitle}>TALEBİNİZ İLETİLİYOR!</div>
            <div style={cs.loadingText}>Lütfen bekleyin, bonus talebiniz inceleniyor.</div>
            <div style={cs.loadingDots}>
              <span style={{ ...cs.ldot, animationDelay: '0s' }}/>
              <span style={{ ...cs.ldot, animationDelay: '0.2s' }}/>
              <span style={{ ...cs.ldot, animationDelay: '0.4s' }}/>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  /* ── Result State ── */
  if (phase === 'result') {
    const isOk  = result.type === 'success';
    const isQueued = result.type === 'queued';
    const isWarn = result.type === 'warning';
    const accent = isOk ? '#4ade80' : (isQueued || isWarn) ? '#fbbf24' : result.type === 'info' ? '#38bdf8' : '#f87171';
    const accentBg = isOk ? 'rgba(74,222,128,0.10)' : (isQueued || isWarn) ? 'rgba(251,191,36,0.10)' : result.type === 'info' ? 'rgba(56,189,248,0.10)' : 'rgba(248,113,113,0.10)';
    const accentBorder = isOk ? 'rgba(74,222,128,0.25)' : (isQueued || isWarn) ? 'rgba(251,191,36,0.25)' : result.type === 'info' ? 'rgba(56,189,248,0.25)' : 'rgba(248,113,113,0.25)';
    const label = isOk ? 'TALEBİNİZ ONAYLANDI' : isQueued ? 'TALEBİNİZ SIRAYA ALINMIŞTIR' : isWarn ? 'UYARI' : result.type === 'info' ? 'BİLGİLENDİRME' : 'HATA';

    return createPortal(
      <div style={overlayStyle} className="modal-overlay-anim">
        <div style={popupStyle} className="modal-popup-anim">
          <div style={cs.resultWrap}>

            {/* Durum ikonu — her zaman göster */}
            <div style={{ ...cs.resultIconRing, background: accentBg, borderColor: accentBorder }}>
              {isOk ? (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/>
                </svg>
              ) : isQueued ? (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              ) : isWarn ? (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.2" strokeLinecap="round">
                  <path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                </svg>
              ) : (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/>
                </svg>
              )}
            </div>

            {/* Durum etiketi */}
            <div style={{ ...cs.resultLabel, color: accent,
              textShadow: `0 0 20px ${accentBg.replace('0.10','0.4')}` }}>
              {label}
            </div>

            {/* Bonus adı */}
            <div style={cs.resultBonusTitle}>{bonus.title}</div>

            {/* Mesaj */}
            <div style={cs.resultMsg}>{result.message}</div>

            {/* TAMAM */}
            <button
              style={{ ...cs.tamamBtn, borderColor: accentBorder,
                boxShadow: `0 0 20px ${accentBg}` }}
              onClick={onClose}
            >
              TAMAM →
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const ImageArea = ({ src, title }) => {
    // GÖRSELSİZ MOD veya yönlendirme linki varsa: görsel yerine glow icon ring — sade ve tutarlı vurgu elemanı
    const useTextLayout = isTextOnly || (redirectUrl && isValidUrl(redirectUrl)) || (bonus && bonus.redirect_link && isValidUrl(bonus.redirect_link));
    if (useTextLayout) {
      // Seçilen bonusun kategori ikonu (Tümü hariç — onda jenerik ikon).
      // Renk: tema birincil rengi (--primary-color) → admin panel temasıyla uyumlu.
      const hasCat = bonus.category && normalizeCat(bonus.category) !== 'tumu';
      return (
        <div style={cs.iconRing}>
          {hasCat ? (
            <span style={{ color: 'var(--primary-color, #38bdf8)', display: 'inline-flex', fontSize: '30px' }}>
              {getCategoryIcon(bonus.category)}
            </span>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 12V8H6a2 2 0 0 1 0-4h12v4"/>
              <path d="M4 6v12a2 2 0 0 0 2 2h14v-4"/>
              <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
            </svg>
          )}
        </div>
      );
    }
    if (imageError) {
      return (
        <div style={placeholderStyle}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(var(--primary-color-rgb),0.5)" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="3"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="M21 15l-5-5L5 21"/>
          </svg>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={title}
        style={imgStyle}
        onError={() => setImageError(true)}
      />
    );
  };

  return createPortal(
    <>
      {alertData.show && createPortal(renderAlert(), document.body)}
      {showRulesInline && createPortal(
        <RulesPopup
          isOpen={showRulesInline}
          onClose={() => setShowRulesInline(false)}
          bonusTitle={bonus.title}
          bonusImage={bonus.image}
          rules={bonus.rules || []}
        />,
        document.body
      )}

      <div style={overlayStyle}>
        <div style={popupStyle}>

          {/* X butonu */}
          <button style={cs.closeBtn} onClick={onClose}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>

          {showRedirectConfirm ? (
            /* ── Yönlendirme Onayı ── */
            <>
              <div style={contentStyle}>
                <ImageArea src={bonus.image} title={bonus.title} />
                <div style={titleStyle}>{bonus.title}</div>
                <div style={questionStyle}>
                  {t('popup.redirectConfirm', 'Yönlendiriliyorsunuz, emin misiniz?')}
                </div>
                {redirectUrl && (
                  <a href={redirectUrl} target="_blank" rel="noopener noreferrer" style={cs.redirectLink}>
                    {redirectUrl}
                  </a>
                )}
              </div>
              <div style={actionsStyle}>
                <button style={cancelBtnStyle} onClick={handleRedirectCancel}>
                  {t('buttons.cancel', 'Vazgeç')}
                </button>
                <button style={confirmBtnStyle} onClick={handleRedirectConfirm}>
                  {t('buttons.confirm', 'Yönlendir')}
                </button>
              </div>
            </>
          ) : (
            /* ── Normal Onay ── */
            <>
              <div style={contentStyle}>
                <ImageArea src={bonus.image} title={bonus.title} />
                <div style={titleStyle}>{bonus.title}</div>
                <div style={questionStyle}>
                  {bonus.talep_text || t('popup.confirmTitle', 'Talebinizi onaylıyor musunuz?')}
                </div>
                {bonus.rules && bonus.rules.length > 0 && (
                  <button style={rulesBtnStyle} onClick={() => setShowRulesInline(true)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 8v4M12 16h.01"/>
                    </svg>
                    {t('buttons.bonusRules', 'Bonus Kuralları')}
                  </button>
                )}
              </div>
              <div style={actionsStyle}>
                <button style={cancelBtnStyle} onClick={onClose} disabled={loading}>
                  {t('buttons.cancel', 'İptal')}
                </button>
                <button style={{ ...confirmBtnStyle, opacity: loading ? 0.7 : 1 }} onClick={handleConfirmBonus} disabled={loading}>
                  {loading ? t('buttons.processing', 'İşleniyor...') : t('buttons.confirm', 'Onayla')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

const confirmStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2,5,14,0.55)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    display: 'flex',
    alignItems: 'safe center',
    justifyContent: 'center',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    zIndex: 9999,
    padding: '20px',
    boxSizing: 'border-box',
  },
  popup: {
    position: 'relative',
    background: 'var(--bonus-confirm-popup-bg-color, rgba(14,165,233,0.08))',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
    border: '1px solid var(--bonus-confirm-popup-border-color, rgba(14,165,233,0.22))',
    borderRadius: '22px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)',
    width: '100%',
    maxWidth: '380px',
    maxHeight: '90svh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
  },
  closeBtn: {
    position: 'absolute',
    top: '14px',
    right: '14px',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    background: 'var(--bonus-confirm-popup-close-icon-bg-color, rgba(255,255,255,0.07))',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'var(--bonus-confirm-popup-close-icon-color, rgba(255,255,255,0.55))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    zIndex: 2,
    transition: 'all 0.2s',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    padding: '22px 22px 16px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
  },
  img: {
    width: '100%',
    maxWidth: '240px',
    height: 'auto',
    aspectRatio: '1 / 1',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '16px',
    border: '1px solid var(--bonus-confirm-popup-image-border-color, rgba(255,255,255,0.10))',
    display: 'block',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
  },
  imgPlaceholder: {
    width: '240px',
    height: '240px',
    aspectRatio: '1 / 1',
    borderRadius: '16px',
    border: '1px solid rgba(var(--primary-color-rgb),0.15)',
    background: 'rgba(var(--primary-color-rgb),0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* GÖRSELSİZ varyantın "image yerine" geçen vurgu elemanı —
     RulesPopup'taki iconRing ile tutarlı stil */
  iconRing: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'radial-gradient(circle at 30% 30%, rgba(var(--primary-color-rgb),0.32), rgba(var(--primary-color-rgb),0.08) 70%)',
    border: '1px solid rgba(var(--primary-color-rgb),0.35)',
    color: 'var(--primary-color, #38bdf8)',
    fontSize: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 32px rgba(var(--primary-color-rgb),0.32), inset 0 1px 0 rgba(255,255,255,0.12)',
    margin: '6px 0 2px',
  },
  title: {
    color: 'var(--bonus-confirm-popup-title-color, #ffffff)',
    fontSize: '20px',
    fontWeight: '700',
    fontFamily: "'Outfit','Inter',sans-serif",
    textAlign: 'center',
    lineHeight: 1.3,
  },
  question: {
    color: 'var(--bonus-confirm-popup-question-color, rgba(255,255,255,0.55))',
    fontSize: '14px',
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  rulesBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'var(--bonus-confirm-popup-rules-btn-bg-color, rgba(14,165,233,0.10))',
    border: '1px solid var(--bonus-confirm-popup-rules-btn-border-color, rgba(14,165,233,0.28))',
    borderRadius: '20px',
    padding: '6px 14px',
    color: 'var(--bonus-confirm-popup-rules-btn-text-color, var(--primary-color))',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    fontFamily: "'Inter',sans-serif",
    transition: 'all 0.2s',
  },
  redirectLink: {
    color: 'var(--primary-color)',
    fontSize: '12px',
    wordBreak: 'break-all',
    textAlign: 'center',
    textDecoration: 'underline',
    opacity: 0.8,
  },
  actions: {
    display: 'flex',
    gap: '10px',
    padding: '12px 22px 20px',
    flexShrink: 0,
  },
  cancelBtn: {
    flex: 1,
    padding: '12px 0',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'var(--bonus-confirm-popup-cancel-bg-color, rgba(255,255,255,0.08))',
    backdropFilter: 'blur(8px)',
    color: 'var(--bonus-confirm-popup-cancel-text-color, rgba(255,255,255,0.80))',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Inter',sans-serif",
    transition: 'all 0.2s',
  },
  confirmBtn: {
    flex: 1,
    padding: '12px 0',
    borderRadius: '12px',
    border: 'none',
    background: 'var(--bonus-confirm-popup-confirm-bg-color, linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%))',
    color: 'var(--bonus-confirm-popup-confirm-text-color, #ffffff)',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Inter',sans-serif",
    boxShadow: '0 6px 20px rgba(var(--primary-color-rgb),0.40)',
    transition: 'all 0.2s',
  },
  /* Loading State */
  loadingWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '12px', padding: '20px 24px 24px',
  },
  loadingBgImg: {
    width: '100%', maxWidth: '260px', height: '190px',
    objectFit: 'contain', display: 'block',
  },
  loadingIconRing: {
    width: '80px', height: '80px', borderRadius: '50%',
    background: 'rgba(var(--primary-color-rgb),0.10)',
    border: '1px solid rgba(var(--primary-color-rgb),0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 30px rgba(var(--primary-color-rgb),0.15)',
  },
  loadingIconInner: {
    width: '56px', height: '56px', borderRadius: '50%',
    background: 'rgba(var(--primary-color-rgb),0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  loadingTitle: {
    color: '#fbbf24', fontSize: '20px', fontWeight: '800',
    fontFamily: "'Outfit','Inter',sans-serif",
    letterSpacing: '0.05em', textAlign: 'center',
    textShadow: '0 0 24px rgba(251,191,36,0.30)',
  },
  loadingText: {
    color: 'rgba(255,255,255,0.38)', fontSize: '13px',
    textAlign: 'center', lineHeight: 1.5,
  },
  loadingDots: { display: 'flex', gap: '8px', alignItems: 'center' },
  ldot: {
    width: '8px', height: '8px', borderRadius: '50%',
    background: 'var(--primary-color)', display: 'inline-block',
    animation: 'ldot 1.2s ease-in-out infinite',
  },
  /* Result State */
  resultWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '16px', padding: '36px 28px 28px',
  },
  resultIconRing: {
    width: '88px', height: '88px', borderRadius: '50%',
    border: '1px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10)',
  },
  resultLabel: {
    fontSize: '18px', fontWeight: '800',
    fontFamily: "'Outfit','Inter',sans-serif",
    letterSpacing: '0.05em', textAlign: 'center',
  },
  resultBonusTitle: {
    color: '#ffffff', fontSize: '16px', fontWeight: '700',
    fontFamily: "'Outfit','Inter',sans-serif",
    textAlign: 'center', lineHeight: 1.3,
  },
  resultMsg: {
    color: 'rgba(255,255,255,0.45)', fontSize: '13px',
    textAlign: 'center', lineHeight: 1.55,
    maxWidth: '280px',
  },
  tamamBtn: {
    padding: '12px 44px', borderRadius: '12px',
    border: '1px solid',
    background: 'rgba(255,255,255,0.07)',
    backdropFilter: 'blur(8px)',
    color: '#ffffff', fontSize: '13px', fontWeight: '700',
    letterSpacing: '0.08em', cursor: 'pointer',
    fontFamily: "'Inter',sans-serif", marginTop: '4px',
    transition: 'all 0.2s',
  },
};

export default BonusConfirmPopup;